if (!customElements.get('main-search')) {
  const MainSearch = class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('.main-search__form');
      this.searchInput = this.querySelector('.main-search__input');
      this.resultsBox = this.querySelector('.main-search__results');
      this.discovery = this.querySelector('[data-search-discovery]');
      this.recentSection = this.querySelector('[data-search-recent]');
      this.recentList = this.querySelector('[data-search-recent-list]');
      this.clearRecentButton = this.querySelector('[data-search-clear]');
      this.minCharacters = Number.parseInt(this.dataset.minCharacters, 10) || 2;
      this.recentLimit = Number.parseInt(this.dataset.recentLimit, 10) || 5;
      this.searchTimeoutThrottle = 300;
      this.searchTimeoutID = -1;
      this.searchAbortController = null;
      this.oldSearchValue = '';
      this.lastTrigger = null;
      this.wasOpen = document.body.classList.contains('show-search');
      this.productCarouselCleanups = [];
      this.productCarouselRefreshers = [];

      if (!this.form || !this.searchInput || !this.resultsBox) return;

      this.setAttribute('aria-hidden', this.wasOpen ? 'false' : 'true');
      document.documentElement.classList.toggle('lintico-search-open', this.wasOpen);

      this.handleInput = this.handleInput.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleResultClick = this.handleResultClick.bind(this);
      this.syncOpenState = this.syncOpenState.bind(this);

      this.searchInput.addEventListener('input', this.handleInput);
      this.form.addEventListener('submit', () => this.saveRecentSearch(this.searchInput.value));
      this.querySelectorAll('.main-search__close').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      this.querySelectorAll('[data-search-term]').forEach((link) => {
        link.addEventListener('click', () => {
          this.saveRecentSearch(link.dataset.searchTerm || link.textContent);
        });
      });
      this.resultsBox.addEventListener('click', this.handleResultClick);

      if (this.clearRecentButton) {
        this.clearRecentButton.addEventListener('click', () => {
          this.writeRecentSearches([]);
          this.renderRecentSearches();
        });
      }

      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('keydown', this.handleKeydown);

      this.bodyObserver = new MutationObserver(this.syncOpenState);
      this.bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });

      this.initializeProductCarousels();
      this.renderRecentSearches();
      this.showDiscovery();
      this.syncOpenState();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.searchInput?.removeEventListener('input', this.handleInput);
      this.resultsBox?.removeEventListener('click', this.handleResultClick);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('keydown', this.handleKeydown);
      this.bodyObserver?.disconnect();
      this.productCarouselCleanups?.forEach((cleanup) => cleanup());
      this.productCarouselCleanups = [];
      this.productCarouselRefreshers = [];
      this.abortSearch();
      clearTimeout(this.searchTimeoutID);
      this.initialized = false;
    }

    initializeProductCarousels() {
      this.querySelectorAll('[data-search-product-carousel]').forEach((carousel) => {
        const scroller = carousel.querySelector('[data-search-carousel-scroller]');
        const previousButton = carousel.querySelector('[data-search-carousel-previous]');
        const nextButton = carousel.querySelector('[data-search-carousel-next]');
        if (!scroller || !previousButton || !nextButton) return;

        let updateFrame = null;
        const updateButtons = () => {
          updateFrame = null;
          const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
          const hasOverflow = maxScrollLeft > 2;
          previousButton.disabled = !hasOverflow || scroller.scrollLeft <= 2;
          nextButton.disabled = !hasOverflow || scroller.scrollLeft >= maxScrollLeft - 2;
          carousel.classList.toggle('search-recommendations--scrollable', hasOverflow);
        };
        const queueButtonUpdate = () => {
          if (updateFrame !== null) return;
          updateFrame = window.requestAnimationFrame(updateButtons);
        };
        const scrollCarousel = (direction) => {
          const distance = Math.max(scroller.clientWidth * 0.9, 1);
          scroller.scrollBy({
            left: distance * direction,
            behavior: 'smooth'
          });
        };
        const handlePrevious = () => scrollCarousel(-1);
        const handleNext = () => scrollCarousel(1);

        previousButton.addEventListener('click', handlePrevious);
        nextButton.addEventListener('click', handleNext);
        scroller.addEventListener('scroll', queueButtonUpdate, { passive: true });
        window.addEventListener('resize', queueButtonUpdate);

        let resizeObserver = null;
        if ('ResizeObserver' in window) {
          resizeObserver = new ResizeObserver(queueButtonUpdate);
          resizeObserver.observe(scroller);
        }

        this.productCarouselRefreshers.push(queueButtonUpdate);
        this.productCarouselCleanups.push(() => {
          previousButton.removeEventListener('click', handlePrevious);
          nextButton.removeEventListener('click', handleNext);
          scroller.removeEventListener('scroll', queueButtonUpdate);
          window.removeEventListener('resize', queueButtonUpdate);
          resizeObserver?.disconnect();
          if (updateFrame !== null) window.cancelAnimationFrame(updateFrame);
        });

        queueButtonUpdate();
      });
    }

    refreshProductCarousels() {
      this.productCarouselRefreshers?.forEach((refresh) => refresh());
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('.show-search-link');
      if (trigger) this.lastTrigger = trigger;
    }

    handleResultClick(event) {
      const link = event.target.closest('a[href]');
      if (!link || !this.resultsBox.contains(link)) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      const targetUrl = new URL(link.href, window.location.origin);
      if (targetUrl.origin !== window.location.origin) return;

      const searchTerm = this.searchInput.value.trim();
      this.saveRecentSearch(searchTerm);
      this.pushSearchResultsHistory(searchTerm, link);
    }

    pushSearchResultsHistory(searchTerm, link) {
      if (!searchTerm || !window.history?.pushState || !this.form) return;

      const returnUrl = new URL(this.form.action, window.location.origin);
      returnUrl.searchParams.set('q', searchTerm);
      returnUrl.searchParams.set('type', this.getSearchReturnType(link));

      const currentUrl = new URL(window.location.href);
      if (currentUrl.pathname === returnUrl.pathname && currentUrl.search === returnUrl.search) {
        return;
      }

      try {
        window.history.pushState(
          {
            linticoSearchReturn: true,
            searchTerm
          },
          '',
          returnUrl.toString()
        );
      } catch (error) {
        // Returning to search results is a progressive enhancement.
      }
    }

    getSearchReturnType(link) {
      if (link.closest('.lintico-predictive-search__section--helpful')) {
        const href = link.getAttribute('href') || '';
        return href.includes('/blogs/') ? 'article' : 'page';
      }

      return 'product';
    }

    syncOpenState() {
      const isOpen = document.body.classList.contains('show-search');
      if (isOpen === this.wasOpen) return;

      this.wasOpen = isOpen;
      this.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      document.documentElement.classList.toggle('lintico-search-open', isOpen);

      if (isOpen) {
        this.renderRecentSearches();
        this.refreshProductCarousels();
        if (this.searchInput.value.trim().length < this.minCharacters) {
          this.showDiscovery();
        }
      } else {
        this.abortSearch();
        clearTimeout(this.searchTimeoutID);
        this.lastTrigger?.focus();
      }
    }

    close() {
      document.body.classList.remove('show-search');
    }

    handleKeydown(event) {
      if (!document.body.classList.contains('show-search')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.getClientRects().length);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleInput() {
      const valueToSearch = this.searchInput.value.trim();

      if (!this.dataset.quickSearch || this.dataset.quickSearch !== 'true') {
        return;
      }

      if (valueToSearch.length < this.minCharacters) {
        this.oldSearchValue = valueToSearch;
        this.abortSearch();
        clearTimeout(this.searchTimeoutID);
        this.showDiscovery();
        return;
      }

      if (valueToSearch === this.oldSearchValue) return;
      this.oldSearchValue = valueToSearch;
      this.abortSearch();
      clearTimeout(this.searchTimeoutID);
      this.showLoading();

      this.searchTimeoutID = window.setTimeout(() => {
        this.fetchResults(valueToSearch);
      }, this.searchTimeoutThrottle);
    }

    async fetchResults(searchTerm) {
      const searchAbortController = new AbortController();
      this.searchAbortController = searchAbortController;
      const includeMeta = this.dataset.quickSearchMeta === 'true';

      const requestUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
      requestUrl.searchParams.set('q', searchTerm);
      requestUrl.searchParams.set('section_id', 'predictive-search');
      requestUrl.searchParams.set('resources[type]', 'query,product,collection,page,article');
      const predictiveResultLimit = window.matchMedia('(min-width: 768px)').matches ? '8' : '6';
      requestUrl.searchParams.set('resources[limit]', predictiveResultLimit);
      requestUrl.searchParams.set('resources[limit_scope]', 'each');
      requestUrl.searchParams.set(
        'resources[options][fields]',
        includeMeta
          ? 'title,product_type,variants.title,vendor,tag,variants.sku'
          : 'title,product_type,variants.title,vendor'
      );

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          signal: searchAbortController.signal
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const html = await response.text();
        const template = document.createElement('template');
        template.innerHTML = html;
        const results = template.content.querySelector('.lintico-predictive-search');

        if (this.searchInput.value.trim() !== searchTerm) return;

        this.resultsBox.replaceChildren();
        if (results) this.resultsBox.appendChild(results.cloneNode(true));

        const hasResults = results?.dataset.hasResults === 'true';
        this.classList.remove('main-search--loading');
        this.classList.toggle('main-search--has-results', hasResults);
        this.classList.toggle('main-search--no-results', !hasResults);
        this.searchInput.setAttribute('aria-expanded', 'true');
        this.resultsBox.setAttribute('aria-busy', 'false');
      } catch (error) {
        if (error.name === 'AbortError') return;

        this.resultsBox.replaceChildren();
        const message = document.createElement('p');
        message.className = 'main-search__empty-message';
        message.textContent =
          theme.strings.generalSearchNoResultsWithoutTerms ||
          'Sorry, we could not load search results.';
        this.resultsBox.appendChild(message);
        this.classList.remove('main-search--loading', 'main-search--has-results');
        this.classList.add('main-search--no-results');
        this.resultsBox.setAttribute('aria-busy', 'false');
      } finally {
        if (this.searchAbortController === searchAbortController) {
          this.searchAbortController = null;
        }
      }
    }

    showLoading() {
      this.classList.remove('main-search--has-results', 'main-search--no-results');
      this.classList.add('main-search--loading');
      this.searchInput.setAttribute('aria-expanded', 'true');
      this.resultsBox.setAttribute('aria-busy', 'true');
      this.resultsBox.innerHTML =
        '<div class="main-search__results-spinner" role="status" aria-label="Loading search results"><div class="loading-spinner"></div></div>';
    }

    showDiscovery() {
      this.classList.remove(
        'main-search--loading',
        'main-search--has-results',
        'main-search--no-results'
      );
      this.searchInput?.setAttribute('aria-expanded', 'false');
      this.resultsBox?.setAttribute('aria-busy', 'false');
      this.resultsBox?.replaceChildren();
    }

    abortSearch() {
      if (!this.searchAbortController) return;
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }

    getRecentSearches() {
      try {
        const value = window.localStorage.getItem('linticoRecentSearches');
        const searches = value ? JSON.parse(value) : [];
        return Array.isArray(searches) ? searches.filter((item) => typeof item === 'string') : [];
      } catch (error) {
        return [];
      }
    }

    writeRecentSearches(searches) {
      try {
        window.localStorage.setItem(
          'linticoRecentSearches',
          JSON.stringify(searches.slice(0, this.recentLimit))
        );
      } catch (error) {
        // Recent searches are an optional enhancement when storage is unavailable.
      }
    }

    saveRecentSearch(value) {
      const searchTerm = String(value || '').trim();
      if (searchTerm.length < this.minCharacters) return;

      const searches = this.getRecentSearches().filter(
        (item) => item.toLocaleLowerCase() !== searchTerm.toLocaleLowerCase()
      );
      searches.unshift(searchTerm);
      this.writeRecentSearches(searches);
    }

    renderRecentSearches() {
      if (!this.recentSection || !this.recentList) return;

      const searches = this.getRecentSearches();
      this.recentList.replaceChildren();
      this.recentSection.hidden = searches.length === 0;

      searches.forEach((searchTerm) => {
        const url = new URL(this.form.action, window.location.origin);
        url.searchParams.set('q', searchTerm);
        url.searchParams.set('type', 'product');

        const link = document.createElement('a');
        link.className = 'main-search__chip';
        link.href = url.toString();
        link.textContent = searchTerm;
        link.addEventListener('click', () => this.saveRecentSearch(searchTerm));
        this.recentList.appendChild(link);
      });
    }
  };

  window.customElements.define('main-search', MainSearch);
}
